import { z } from 'zod';

/**
 * Shared proposal validity discriminator.
 *
 * `schema_invalid` identifies persisted rows that fail structural parsing;
 * `domain_invalid` identifies structurally valid proposals that fail current-state checks.
 */
export const validProposalValiditySchema = z.literal('valid').default('valid');

export const invalidProposalValiditySchema = z.enum([
  'schema_invalid',
  'domain_invalid',
]);

export type InvalidProposalValidity = z.infer<
  typeof invalidProposalValiditySchema
>;
export type ProposalValidity = 'valid' | InvalidProposalValidity;

/**
 * Stable current-state validation reasons shared by server and clients.
 * Values are API-safe identifiers; default client display copy is defined below.
 */
export enum DomainInvalidReason {
  REFERENCED_ADMIN_USER_MISSING = 'referenced_admin_user_missing',
  REFERENCED_ADMIN_USER_NO_LONGER_IN_ORGANIZATION = 'referenced_admin_user_no_longer_in_organization',
  ADDED_USER_ALREADY_ADMIN = 'added_user_already_admin',
  REMOVED_USER_NO_LONGER_ADMIN = 'removed_user_no_longer_admin',
  PROPOSAL_WOULD_REMOVE_LAST_ADMIN = 'proposal_would_remove_last_admin',
  APPROVAL_THRESHOLD_EXCEEDS_RESULTING_ADMIN_COUNT = 'approval_threshold_exceeds_resulting_admin_count',
  TARGET_RECOVERY_NO_LONGER_INITIATED = 'target_recovery_no_longer_initiated',
  TARGET_GROUP_MISSING = 'target_group_missing',
  TARGET_GROUP_BELONGS_TO_DIFFERENT_ORGANIZATION = 'target_group_belongs_to_different_organization',
  GROUP_REFERENCED_BY_ACTIVE_POLICY = 'group_referenced_by_active_policy',
  REFERENCED_USER_MISSING = 'referenced_user_missing',
  ADDED_USER_NO_LONGER_IN_ORGANIZATION = 'added_user_no_longer_in_organization',
  ADDED_USER_ALREADY_IN_GROUP = 'added_user_already_in_group',
  REMOVED_USER_NO_LONGER_IN_GROUP = 'removed_user_no_longer_in_group',
  TARGET_ACCOUNT_BELONGS_TO_DIFFERENT_ORGANIZATION = 'target_account_belongs_to_different_organization',
  REQUESTED_NETWORK_ALREADY_ADDED = 'requested_network_already_added',
  AFFECTED_USER_MISSING = 'affected_user_missing',
  JOINING_USER_ALREADY_IN_ORGANIZATION = 'joining_user_already_in_organization',
  REMOVING_USER_NO_LONGER_IN_ORGANIZATION = 'removing_user_no_longer_in_organization',
  TARGET_POLICY_BELONGS_TO_DIFFERENT_ORGANIZATION = 'target_policy_belongs_to_different_organization',
  ADDED_ACCOUNT_ALREADY_ON_POLICY = 'added_account_already_on_policy',
  REMOVED_ACCOUNT_NO_LONGER_ON_POLICY = 'removed_account_no_longer_on_policy',
  REFERENCED_ACCOUNT_BELONGS_TO_DIFFERENT_ORGANIZATION = 'referenced_account_belongs_to_different_organization',
  REFERENCED_POLICY_USER_MISSING = 'referenced_policy_user_missing',
  REFERENCED_POLICY_USER_NO_LONGER_IN_ORGANIZATION = 'referenced_policy_user_no_longer_in_organization',
  REFERENCED_POLICY_GROUP_MISSING = 'referenced_policy_group_missing',
  REFERENCED_POLICY_GROUP_BELONGS_TO_DIFFERENT_ORGANIZATION = 'referenced_policy_group_belongs_to_different_organization',
  APPROVER_THRESHOLD_EXCEEDS_GROUP_MEMBER_COUNT = 'approver_threshold_exceeds_group_member_count',
}

const domainInvalidReasonDisplayCopy: Record<DomainInvalidReason, string> = {
  [DomainInvalidReason.REFERENCED_ADMIN_USER_MISSING]:
    'An administrator referenced by this proposal no longer exists.',
  [DomainInvalidReason.REFERENCED_ADMIN_USER_NO_LONGER_IN_ORGANIZATION]:
    'An administrator referenced by this proposal is no longer in the organization.',
  [DomainInvalidReason.ADDED_USER_ALREADY_ADMIN]:
    'A user this proposal would add is already an administrator.',
  [DomainInvalidReason.REMOVED_USER_NO_LONGER_ADMIN]:
    'A user this proposal would remove is no longer an administrator.',
  [DomainInvalidReason.PROPOSAL_WOULD_REMOVE_LAST_ADMIN]:
    'This proposal would remove the organization’s last administrator.',
  [DomainInvalidReason.APPROVAL_THRESHOLD_EXCEEDS_RESULTING_ADMIN_COUNT]:
    'The proposed approval threshold is greater than the resulting administrator count.',
  [DomainInvalidReason.TARGET_RECOVERY_NO_LONGER_INITIATED]:
    'The targeted recovery is no longer awaiting cancellation or finalization.',
  [DomainInvalidReason.TARGET_GROUP_MISSING]:
    'The group targeted by this proposal no longer exists.',
  [DomainInvalidReason.TARGET_GROUP_BELONGS_TO_DIFFERENT_ORGANIZATION]:
    'The group targeted by this proposal no longer belongs to this organization.',
  [DomainInvalidReason.GROUP_REFERENCED_BY_ACTIVE_POLICY]:
    'This group is used by an active policy. Update or delete that policy first.',
  [DomainInvalidReason.REFERENCED_USER_MISSING]:
    'A user referenced by this group proposal no longer exists.',
  [DomainInvalidReason.ADDED_USER_NO_LONGER_IN_ORGANIZATION]:
    'A user this proposal would add to the group is no longer in the organization.',
  [DomainInvalidReason.ADDED_USER_ALREADY_IN_GROUP]:
    'A user this proposal would add is already in the group.',
  [DomainInvalidReason.REMOVED_USER_NO_LONGER_IN_GROUP]:
    'A user this proposal would remove is no longer in the group.',
  [DomainInvalidReason.TARGET_ACCOUNT_BELONGS_TO_DIFFERENT_ORGANIZATION]:
    'The account targeted by this proposal no longer belongs to this organization.',
  [DomainInvalidReason.REQUESTED_NETWORK_ALREADY_ADDED]:
    'The requested network has already been added to this account.',
  [DomainInvalidReason.AFFECTED_USER_MISSING]:
    'The member targeted by this proposal no longer exists.',
  [DomainInvalidReason.JOINING_USER_ALREADY_IN_ORGANIZATION]:
    'The joining user is already a member of an organization.',
  [DomainInvalidReason.REMOVING_USER_NO_LONGER_IN_ORGANIZATION]:
    'The member this proposal would remove is no longer in the organization.',
  [DomainInvalidReason.TARGET_POLICY_BELONGS_TO_DIFFERENT_ORGANIZATION]:
    'The policy targeted by this proposal no longer belongs to this organization.',
  [DomainInvalidReason.ADDED_ACCOUNT_ALREADY_ON_POLICY]:
    'An account this proposal would add is already on the policy.',
  [DomainInvalidReason.REMOVED_ACCOUNT_NO_LONGER_ON_POLICY]:
    'An account this proposal would remove is no longer on the policy.',
  [DomainInvalidReason.REFERENCED_ACCOUNT_BELONGS_TO_DIFFERENT_ORGANIZATION]:
    'An account referenced by this proposal no longer belongs to this organization.',
  [DomainInvalidReason.REFERENCED_POLICY_USER_MISSING]:
    'A user referenced by this policy proposal no longer exists.',
  [DomainInvalidReason.REFERENCED_POLICY_USER_NO_LONGER_IN_ORGANIZATION]:
    'A user referenced by this policy proposal is no longer in the organization.',
  [DomainInvalidReason.REFERENCED_POLICY_GROUP_MISSING]:
    'A group referenced by this policy proposal no longer exists.',
  [DomainInvalidReason.REFERENCED_POLICY_GROUP_BELONGS_TO_DIFFERENT_ORGANIZATION]:
    'A group referenced by this policy proposal no longer belongs to this organization.',
  [DomainInvalidReason.APPROVER_THRESHOLD_EXCEEDS_GROUP_MEMBER_COUNT]:
    'The proposed approval threshold is greater than the approver group’s current member count.',
};

/**
 * Returns default client display copy for an invalid proposal. Schema-invalid
 * proposals have no domain reason and therefore use the generic fallback.
 */
export function getInvalidProposalDisplayMessage(
  reason?: DomainInvalidReason
): string {
  // Case: Schema-invalid proposals have no domain reason, so clients receive generic reject-only copy.
  if (!reason) {
    return 'This proposal is invalid and can only be rejected.';
  }

  return `${domainInvalidReasonDisplayCopy[reason]} This proposal can only be rejected.`;
}

/** Shared error identity for attempts to approve any invalid proposal. */
export const invalidProposalRejectOnlyError = {
  code: 'invalid',
  cause: 'proposal_invalidated',
  message: 'Invalid proposals can only be rejected.',
} as const;
